import { useEffect, useRef, useCallback, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'wizard';
  footer?: ReactNode;
  /** Slot rendered in the header, between title and close button.
   *  Use for actions like Activity log, refresh, etc. */
  headerExtra?: ReactNode;
}

// Modal width tiers. Mobile (<sm) always full-width minus overlay padding so
// the panel never overflows the viewport. Larger breakpoints scale toward the
// configured target width.
const SIZE_CLASSES: Record<string, string> = {
  sm: 'w-full max-w-md',
  md: 'w-full sm:w-[90vw] sm:max-w-[640px] md:w-[70vw] md:max-w-[720px] lg:max-w-[50vw]',
  lg: 'w-full sm:w-[92vw] sm:max-w-[720px] md:w-[80vw] md:max-w-[900px] lg:max-w-[60vw]',
  xl: 'w-full sm:w-[94vw] sm:max-w-[860px] md:w-[88vw] md:max-w-[1080px] lg:max-w-[75vw]',
  wide: 'w-full sm:w-[96vw] sm:max-w-[1080px] lg:max-w-[90vw]',
  // Wizard panel — a constant 80% of the viewport in BOTH dimensions (80vw ×
  // 80vh) so multi-step wizards don't grow/shrink per step (the body scrolls
  // instead). 80vw stays true on typical laptops/desktops (≤2000px); the
  // 1600px ceiling only kicks in on ultra-wide / 4K so single-column fields and
  // the reviewer table don't stretch the full screen. Full-width on the
  // smallest screens where 80vw would be too cramped.
  wizard: 'w-full sm:w-[80vw] sm:max-w-[1600px] h-[80vh]',
};

export default function Modal({ open, onClose, title, children, size = 'md', footer, headerExtra }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  // Stable onClose ref so the open-effect doesn't re-fire on every parent re-render.
  // Inline arrow callbacks from the parent change identity each render — without this
  // ref, every keystroke in any focused child input would trigger the effect, refocus
  // the dialog wrapper, and steal focus away from the input.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
      previouslyFocused?.focus();
    };
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  return createPortal(
    <div className="z-modal__overlay">
      <div className="z-modal__backdrop" onClick={onClose} />
      <div
        ref={dialogRef}
        className={cn('z-modal', SIZE_CLASSES[size])}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className="z-modal__header">
          <div id={titleId} className="z-modal__title flex-1 min-w-0">{title}</div>
          {headerExtra && <div className="flex items-center mr-2">{headerExtra}</div>}
          <button type="button" onClick={onClose} className="z-modal__close">
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>
        <div className="z-modal__body">
          {children}
        </div>
        {footer && (
          <div className="z-modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
